// utils/pdfCoverUtils.js
//
// The web half of the app's utils/pdfCoverUtils.js. Same limits, same
// messages, so a PDF the app accepts is accepted here and one it rejects is
// rejected for the same stated reason.

/** The app's ceiling. Above this, conversion times get unreasonable. */
export const MAX_PDF_BYTES = 50 * 1024 * 1024;

export function formatFileSize(bytes) {
  if (!bytes) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${Math.round((bytes / Math.pow(k, i)) * 100) / 100} ${sizes[i]}`;
}

/**
 * @param {File} file
 * @returns {{valid: boolean, error?: string}}
 */
export function validatePdfFile(file) {
  if (!file) return { valid: false, error: 'No file selected' };

  // A browser reports the type from the extension as often as from the bytes,
  // so both are checked — a .pdf renamed from something else fails on the
  // server, and failing here says so sooner.
  const looksLikePdf =
    file.type === 'application/pdf' || /\.pdf$/i.test(file.name || '');

  if (!looksLikePdf) return { valid: false, error: 'Please select a PDF file' };

  if (file.size > MAX_PDF_BYTES) {
    return { valid: false, error: 'PDF file must be less than 50MB' };
  }

  return { valid: true };
}

/**
 * A File as base64, without the `data:` prefix.
 *
 * The conversion service takes base64 in a JSON body, which is what the app
 * sends it. Base64 is a third larger than the bytes, so a 50MB PDF becomes a
 * ~67MB request — within the service's 50mb express limit only because that
 * limit is applied to the parsed body, not the encoding. Anything near the
 * ceiling is slow either way, which is what the size limit is for.
 */
export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const comma = result.indexOf(',');
      resolve(comma === -1 ? result : result.slice(comma + 1));
    };
    reader.onerror = () => reject(new Error('Could not read that PDF.'));
    reader.readAsDataURL(file);
  });
}

/**
 * How many pages, read out of the PDF itself.
 *
 * There is no pdf.js in this bundle, and adding it to count pages would be a
 * several-megabyte dependency for one number. A PDF's page tree root carries
 * `/Count N`, and the largest such number in the file is the total — nested
 * page-tree nodes carry their own smaller counts. That holds for ordinary
 * PDFs and gives up on linearised or object-stream-compressed ones, where the
 * count is inside a compressed stream.
 *
 * So this is a hint, not a fact: null means "could not tell", and the caller
 * shows nothing rather than a wrong number. The real count comes back from
 * the conversion service, which renders every page.
 *
 * @returns {Promise<number|null>}
 */
export async function guessPageCount(file) {
  try {
    // The page tree is usually near the end, but /Count can appear anywhere;
    // 2MB from the front covers the overwhelming majority without reading a
    // 50MB file into memory to show a number.
    const slice = file.slice(0, Math.min(file.size, 2 * 1024 * 1024));

    // FileReader rather than Blob.arrayBuffer(): the latter is missing in
    // Safari before 14 and in jsdom, and the failure is silent — the catch
    // below turns it into "no page count" on browsers that are otherwise
    // perfectly capable. FileReader has been everywhere for a decade.
    const buffer = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(reader.error);
      reader.readAsArrayBuffer(slice);
    });

    // latin1 maps every byte to one code unit, so binary stretches cannot
    // swallow the ASCII markers the way a UTF-8 decode would.
    const text = new TextDecoder('latin1').decode(new Uint8Array(buffer));

    const counts = [...text.matchAll(/\/Count\s+(\d+)/g)].map((m) => Number(m[1]));
    if (counts.length === 0) return null;

    const max = Math.max(...counts);
    return Number.isFinite(max) && max > 0 ? max : null;
  } catch {
    return null;
  }
}
